import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import GoalGrid from './Components/GoalGrid';
import GoalDetail from './Components/GoalDetail';
import LoadingScreen from './Components/LoadingScreen';
import ErrorScreen from './Components/ErrorScreen';
import EUUnderConstructionPage from './Components/EUUnderConstructionPage';
import { isEuSdgEnabled } from './utils/featureFlags';
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from './Components/Header';
import Footer from './Components/Footer';
import DetailBackBar from './Components/DetailBackBar';
import { GoalsProvider }  from './State/GoalsProvider';
import { GoalsContext } from './State/GoalsContext';
import { GeneralDataProvider } from './State/GeneralDataProvider';
import { GeneralDataContext } from './State/GeneralDataContext';
import TargetsProvider from './State/TargetsProvider';
import { IndicatorsProvider } from './State/IndicatorsProvider';
import AlignmentPage from './Components/AlignmentPage';
import AllGoalIndicators from './Components/AllGoalIndicators.js';
import Contact from './Components/Contact';
import SDGProfilePage from './Components/SDGProfilePage';
import IndicatorsCatalogPage from './Components/IndicatorsCatalogPage';

const AppShell = ({ children }) => (
  <div className="App">
    <Header />
    <DetailBackBar />
    <main className="flex-grow-1">{children}</main>
    <Footer />
  </div>
);

// Component that uses the context - must be inside providers
const AppContent = () => {
  const { initialLoading: goalsInitialLoading, error: goalsError, fetchGoals } = useContext(GoalsContext);
  const {
    data: generalData,
    initialLoading: generalDataInitialLoading,
    error: generalDataError,
    refresh: refreshGeneralData,
  } = useContext(GeneralDataContext);
  const location = useLocation();

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleRetry = () => {
    fetchGoals();
    refreshGeneralData();
  };

  const hasError = (!goalsInitialLoading && goalsError) || (!generalDataInitialLoading && generalDataError);
  const errorMessage = goalsError || generalDataError || 'There seems to be an issue with the data server. Please try again later.';

  if (hasError && !goalsInitialLoading && !generalDataInitialLoading) {
    return <ErrorScreen onRetry={handleRetry} errorMessage={errorMessage} />;
  }

  if (goalsInitialLoading || generalDataInitialLoading) {
    return <LoadingScreen />;
  }

  const isEuPath = location.pathname === '/eu-sdg' || location.pathname.startsWith('/eu-sdg/');
  const showEuUnderConstruction = isEuPath && !isEuSdgEnabled;

  if (showEuUnderConstruction) {
    return (
      <AppShell>
        <EUUnderConstructionPage />
      </AppShell>
    );
  }

  return (
    <TargetsProvider>
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/un-sdg" replace />} />
          <Route path="/un-sdg" element={<GoalGrid />} />
          <Route path="/eu-sdg" element={<GoalGrid />} />
          <Route path="/:goalType/indicators-catalog" element={<IndicatorsCatalogPage />} />
          <Route path="/:goalType/goal/:goalId/indicators" element={<AllGoalIndicators />} />
          <Route path="/:goalType/goal/:goalId/indicators/:indicatorId?" element={<AllGoalIndicators />} />
          <Route path="/:goalType/goal/:goalId/targets/:targetId/*" element={<GoalDetail />} />
          <Route path="/:goalType/goal/:goalId/targets" element={<GoalDetail />} />
          <Route path="/:goalType/goal/:goalId/*" element={<GoalDetail />} />
          <Route path="/alignment" element={<AlignmentPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/:goalType/sdg-profile" element={<SDGProfilePage />} />
          <Route path="/sdg-profile" element={<SDGProfilePage />} />
          <Route path="/profile" element={<SDGProfilePage />} />
        </Routes>
      </AppShell>
    </TargetsProvider>
  );
};

function App() {
  return (
    <GoalsProvider>
      <GeneralDataProvider>
        <IndicatorsProvider>
          <AppContent />
        </IndicatorsProvider>
      </GeneralDataProvider>
    </GoalsProvider>
  );
}

export default App;
